<script>
import baseMixin from '@mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import { isEqual, orderBy } from 'lodash'
import TreeViewDto from '@/models/app/treeViewDto'
import ClientSelectorTreeViewDto from '@/models/clients/clientSelectorTreeViewDto'
import { LinkType } from '@/shared/constants/permissions/LinkType'

export default {
  name: 'TheClientSelectorList',
  mixins: [baseMixin],
  props: {
    search: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      model: [],
      activeList: [],
      initialSelection: [],
    }
  },
  computed: {
    ...mapGetters({
      permissionsTreeView: 'auth/permissionsTreeView',
      getNodeHierarchyByIdAndLevel: 'auth/getNodeHierarchyByIdAndLevel',
      getSelectedClients: 'client/getSelectedClients',
    }),
    hasSelectionChanged() {
      return !isEqual(
        orderBy(this.initialSelection),
        orderBy(this.model.map((clientNode) => clientNode.id))
      )
    },
  },
  mounted() {
    for (const group of this.permissionsTreeView) {
      const preSelectedClients = group.children.filter((client) =>
        this.getSelectedClients.includes(client.id)
      )

      this.model = this.model.concat(preSelectedClients)
      this.updateList(preSelectedClients)
    }

    this.setCurrentSelectionAsNewInitialValue()
  },
  methods: {
    ...mapActions({
      setClientList: 'client/setClientList',
    }),
    hasGroup(group) {
      return group.groupId !== -1
    },
    setCurrentSelectionAsNewInitialValue() {
      this.initialSelection = this.model.map((clientNode) => clientNode.id)
    },
    saveClientSelection() {
      this.setClientList(this.model.map((clientNode) => clientNode.id))
      this.setCurrentSelectionAsNewInitialValue()
      this.$emit('onSave')
    },
    deselectClientsByIds(clientIds) {
      this.model = this.model.filter(
        (selectedItem) => !clientIds.includes(selectedItem.id)
      )
      this.activeList = this.activeList.filter(
        (selectedItem) => !clientIds.includes(selectedItem.id)
      )
    },
    selectClients(clients) {
      for (const client of clients) {
        this.model.push(client)
        this.activeList.push(client)
      }
    },
    selectClientGroups(clientGroups) {
      for (const group of clientGroups) {
        this.activeList.push(group)
      }
    },
    deselectClientGroupById(groupId) {
      this.model = this.model.filter(
        (selectedItem) => selectedItem.id !== groupId
      )
      this.activeList = this.activeList.filter(
        (selectedItem) => selectedItem.id !== groupId
      )
    },
    getSelectedChildrenFromGroup(groupId) {
      const group = this.permissionsTreeView.find(
        (group) => group.id === groupId
      )

      const clientIds = group.children.map((child) => child.id)

      return this.model.filter((selectedItem) =>
        clientIds.includes(selectedItem.id)
      )
    },
    isClientTheOnlyOneSelectedInGroup(clientId, groupId) {
      const currentlySelectedClientsInGroup = this.getSelectedChildrenFromGroup(
        groupId
      )
      return currentlySelectedClientsInGroup.every(
        (selectedClient) => selectedClient.id === clientId
      )
    },
    handleNodeClick(selectedNode) {
      // Handle clicking on a client group node.
      // The idea is to mimic the multi-select checkbox functionality:
      //   1. If all children are selected, deselect all
      //   2. If some or no children are selected, select all
      if (selectedNode instanceof TreeViewDto) {
        const childrenIds = selectedNode.children.map((child) => child.id)

        const haveAllChildrenBeenSelected = childrenIds.every((id) =>
          this.model.find((selectedItem) => selectedItem.id === id)
        )

        // Deselect all children and group to make selection logic simpler
        this.deselectClientsByIds(childrenIds)
        this.deselectClientGroupById(selectedNode.id)

        // Select all children in a scenario where some or no children have been selected for this client group
        if (!haveAllChildrenBeenSelected) {
          this.selectClients(selectedNode.children)
          this.selectClientGroups([selectedNode])
        }
      }
      // Handle clicking on a client node.
      // If the client is the only one selected, upon deselect we'll also deselect the client group as well.
      else if (selectedNode instanceof ClientSelectorTreeViewDto) {
        // Get the client group id the client belongs to
        const hierarchy = this.getNodeHierarchyByIdAndLevel(
          selectedNode.id,
          LinkType.CLIENT
        )

        if (!hierarchy)
          throw new Error(
            `Could not find hierarchy for client ${selectedNode.id}`
          )

        const isCurrentlySelected = !!this.model.find(
          (selectedItem) => selectedItem.id === selectedNode.id
        )

        const isGroupCurrentlySelected = !!this.activeList.find(
          (activeItem) => activeItem.id === hierarchy.clientGroupId
        )

        if (isCurrentlySelected) {
          // Deselect the client and group (if applicable)
          if (
            this.isClientTheOnlyOneSelectedInGroup(
              selectedNode.id,
              hierarchy.clientGroupId
            )
          ) {
            this.deselectClientGroupById(hierarchy.clientGroupId)
          }
          this.deselectClientsByIds([selectedNode.id])
        } else {
          // Select the client and group (if applicable)
          if (!isGroupCurrentlySelected)
            this.selectClientGroups([
              this.permissionsTreeView.find(
                (group) => group.id === hierarchy.clientGroupId
              ),
            ])
          this.selectClients([selectedNode])
        }
      }
    },
    updateList(list) {
      this.activeList = []

      const groupsToActivate = []

      if (!list || list.length === 0) return

      // Compile list of client groups to activate
      for (const selectedItem of list) {
        this.activeList.push(selectedItem)

        const hierarchy = this.getNodeHierarchyByIdAndLevel(
          selectedItem.id,
          LinkType.CLIENT
        )

        if (!hierarchy)
          throw new Error(
            `Could not find hierarchy for client ${selectedItem.id}`
          )

        if (
          groupsToActivate.find((group) => group.id === hierarchy.clientGroupId)
        )
          continue

        groupsToActivate.push(
          this.permissionsTreeView.find(
            (group) => group.id === hierarchy.clientGroupId
          )
        )
      }

      if (groupsToActivate.length > 0) this.selectClientGroups(groupsToActivate)
    },
    clearSelection() {
      this.model = []
      this.activeList = []
    },
  },
}
</script>

<template>
  <div>
    <v-treeview
      v-model="model"
      style="max-height: 340px; overflow-y: auto;"
      :active="activeList"
      color="primary"
      class="cursor-pointer"
      selectable
      :search="search"
      multiple-active
      hoverable
      return-object
      open-all
      :items="permissionsTreeView"
      @input="updateList"
    >
      <template v-slot:label="{ item }">
        <div class="py-4" @click="handleNodeClick(item)">
          <p class="ml-2 mb-0" :title="item.name">{{
            item.name | truncateText(30)
          }}</p>

          <div
            v-for="(location, index) in item.locations"
            :key="`the-client-selector-list_location_${location}`"
            class="text-caption ml-2"
          >
            <p v-if="index < 5" class="my-0">{{
              location | truncateText(30)
            }}</p>
            <p v-else-if="index === 5" class="my-0">{{
              `+ ${item.locations.length - index} more`
            }}</p>
          </div>
        </div>
      </template>
    </v-treeview>
    <div class="d-flex justify-end py-2 px-4">
      <v-spacer />
      <v-btn text small class="mr-2" @click="clearSelection">
        {{ $t('general.clear') }}
      </v-btn>
      <v-btn
        small
        :disabled="model.length === 0 || !hasSelectionChanged"
        depressed
        color="primary"
        @click="saveClientSelection"
      >
        {{ $t('general.save') }}
        <span v-if="model.length > 0 && hasSelectionChanged" class="ml-1">
          ({{ model.length }})</span
        >
      </v-btn>
    </div>
  </div>
</template>
